import { Injectable, inject } from '@angular/core';
import { Subject, lastValueFrom, map } from 'rxjs';
import { ProductControllerService, ProductKitComponent } from 'src/app/admin-api';

export interface ProductKitLookup {
  isKit: boolean;
  components: Array<ProductKitComponent>;
}

@Injectable({ providedIn: 'root' })
export class ProductKitLookupService {
  private readonly productService = inject(ProductControllerService);
  private readonly cache = new Map<number, ProductKitLookup>();
  private readonly queued = new Set<number>();
  private readonly queue: number[] = [];
  private active = 0;
  private readonly maxConcurrent = 3;
  private readonly updatedSubject = new Subject<void>();

  readonly updated$ = this.updatedSubject.asObservable();

  lookupFor(productVariantId: number | undefined): ProductKitLookup | undefined {
    if (!productVariantId) {
      return undefined;
    }
    return this.cache.get(productVariantId);
  }

  enqueue(productVariantIds: Array<number | undefined | null>): void {
    for (const id of productVariantIds) {
      if (!id || this.cache.has(id) || this.queued.has(id)) {
        continue;
      }
      this.queued.add(id);
      this.queue.push(id);
    }
    this.pump();
  }

  private pump(): void {
    while (this.active < this.maxConcurrent && this.queue.length > 0) {
      const productVariantId = this.queue.shift();
      if (!productVariantId) {
        continue;
      }
      this.active += 1;
      void this.fetchOne(productVariantId)
        .catch(() => {
          this.cache.set(productVariantId, { isKit: false, components: [] });
        })
        .finally(() => {
          this.queued.delete(productVariantId);
          this.active -= 1;
          this.updatedSubject.next();
          this.pump();
        });
    }
  }

  private async fetchOne(productVariantId: number): Promise<void> {
    const lookup = await lastValueFrom(
      this.productService.findProductVariantById(productVariantId).pipe(
        map((data) => ({
          isKit: !!data.result?.isKit,
          components: data.result?.kitComponents || []
        }))
      )
    );
    this.cache.set(productVariantId, lookup);
  }
}
